import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/images/logo.png';
import Field, { TextInput } from '../components/ui/Field';
import Btn from '../components/ui/Btn';
import AgentDot from '../components/ui/AgentDot';
import { validateRequired, validateEmail } from '../utils/validations';
import { toastError, toastSuccess } from '../utils/toast';

// El equipo de agentes que recibe a quien entra. Reaccionan al formulario: miran mientras escribes el correo,
// cierran los ojos con la contraseña y piensan mientras se valida.
const CREW = [
  { name: 'Nora', palette: 'aurora', size: 132, x: '38%', y: '30%', d: 0 },
  { name: 'Leo', palette: 'citrus', size: 84, x: '10%', y: '14%', d: 0.6 },
  { name: 'Mía', palette: 'berry', size: 96, x: '66%', y: '8%', d: 1.1 },
  { name: 'Teo', palette: 'lagoon', size: 70, x: '14%', y: '62%', d: 0.3 },
  { name: 'Sol', palette: 'mint', size: 60, x: '72%', y: '60%', d: 0.9 },
  { name: 'Ari', palette: 'ember', size: 46, x: '48%', y: '74%', d: 1.4 },
];

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState('');
  const [err, setErr] = useState({ email: '', password: '' });

  const check = () => {
    const e = validateRequired(email) || validateEmail(email) || '';
    const p = validateRequired(password) || '';
    setErr({ email: e, password: p });
    return !e && !p;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!check()) return;
    setLoading(true);
    try {
      const user = await login(email, password);
      toastSuccess('Bienvenido ' + (user.user_metadata?.full_name || user.email));
    } catch (e) {
      toastError(e.message || 'No se pudo iniciar sesión. Revisa tu correo y contraseña.');
    } finally {
      setLoading(false);
    }
  };

  const crewState = loading ? 'thinking' : focus === 'password' && !show ? 'shy' : focus === 'email' ? 'happy' : 'idle';

  return (
    <div className="min-h-screen bg-ink text-fg grid lg:grid-cols-[1.1fr_1fr]">
      <section aria-hidden className="relative hidden lg:block overflow-hidden bg-panel border-r border-line">
        <div className="absolute inset-0 opacity-60" style={{ background: 'radial-gradient(60% 50% at 40% 40%, rgb(124 92 255 / 0.16), transparent), radial-gradient(40% 40% at 75% 75%, rgb(43 217 254 / 0.12), transparent)' }} />
        <div className="absolute inset-x-12 top-12 bottom-48">
          {CREW.map((c) => (
            <motion.div key={c.name} className="absolute" style={{ left: c.x, top: c.y }}
              initial={{ opacity: 0, scale: 0.6, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.15 + c.d * 0.3, type: 'spring', stiffness: 160, damping: 14 }}>
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5 + c.d * 2, repeat: Infinity, ease: 'easeInOut', delay: c.d }}>
                <AgentDot name={c.name} palette={c.palette} size={c.size} state={crewState} />
              </motion.div>
            </motion.div>
          ))}
        </div>
        <div className="absolute left-12 right-12 bottom-12">
          <p className="font-display text-[44px] leading-[1.02] font-bold text-fg max-w-[14ch]">Crea agentes que atienden por ti.</p>
          <p className="mt-3 text-[15px] text-fg-dim max-w-[44ch]">Instrucciones, canales y motor en un solo lugar: publícalos en el chat web, Telegram o WhatsApp de cada cliente.</p>
        </div>
      </section>

      <main className="flex items-center justify-center px-5 py-12">
        <motion.div className="w-full max-w-[380px]" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
          <img src={logo} alt="Cantera Digital" className="sidebar-logo h-10 object-contain" />
          <div className="lg:hidden mt-8 flex items-end gap-2" aria-hidden>
            {CREW.slice(0, 4).map((c) => <AgentDot key={c.name} name={c.name} palette={c.palette} size={Math.round(c.size / 2.4)} state={crewState} />)}
          </div>
          <h1 className="mt-8 font-display text-[32px] leading-tight font-bold">Inicia sesión</h1>
          <p className="mt-1.5 text-[15px] text-fg-dim">Panel de administración de la plataforma de agentes.</p>

          <form onSubmit={submit} noValidate className="mt-8 space-y-4">
            <Field label="Correo electrónico" error={err.email}>
              <TextInput type="email" autoComplete="email" value={email} placeholder="tu@canteradigital.mx" className="h-12 text-[15px]"
                onChange={(e) => setEmail(e.target.value)} onFocus={() => setFocus('email')} onBlur={() => setFocus('')} />
            </Field>
            <Field label="Contraseña" error={err.password} adornment={
              <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Ocultar la contraseña' : 'Mostrar la contraseña'} aria-pressed={show}
                className="h-9 w-9 grid place-items-center rounded-full text-fg-faint hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70">
                {show ? <FaEyeSlash aria-hidden /> : <FaEye aria-hidden />}
              </button>
            }>
              <TextInput type={show ? 'text' : 'password'} autoComplete="current-password" value={password} className="h-12 pr-12 text-[15px]"
                onChange={(e) => setPassword(e.target.value)} onFocus={() => setFocus('password')} onBlur={() => setFocus('')} />
            </Field>
            <Btn type="submit" variant="primary" size="lg" loading={loading} className="w-full !mt-6">Iniciar sesión</Btn>
          </form>
        </motion.div>
      </main>
    </div>
  );
};

export default LoginPage;
